﻿import styles from '../rootPage/RootPage.module.scss';
import NotificationIcon from '../../../assets/icons/notification.svg';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { Notification, NotificationsModal } from './NotificationModal.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import {
  GetNotificationsResponseDto,
  NotificationQuery,
  NotificationType,
  RepairsQuery,
  UserQuery,
} from 'services/api/api-client.ts';
import { useNavigate } from 'react-router';
import { getCurrentSupportedLanguage } from '../../../application/localization/localization.ts';
import { tDynamic } from '../../../helpers/tDynamic.ts';
import { queryClient } from '../../../services/api/query-client-helper.ts';
import { getStatus } from '../repairs/components/statuses.ts';
import { refetch } from '../repairs/DetailedRepairs.tsx';

const PAGE_SIZE = 20;

export const Notifications = () => {
  const [isNotificationModalShown, setIsNotificationModalShown] =
    useState(false);

  const [items, setItems] = useState<Notification[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState<NotificationType[]>([]);
  const hasInitializedOptionsSync = useRef(false);
  const offsetRef = useRef(0);

  const navigate = useNavigate();

  const i18n = useScopedTranslation('Page.NotificationModal');
  const i18nRepairs = useScopedTranslation('Page.Repairs');
  const currentLanguage = getCurrentSupportedLanguage(i18n.i18n);

  const notificationQuery = NotificationQuery.useGetQuery(
    {
      language: currentLanguage,
      limit: PAGE_SIZE,
      offset,
      types: selectedTypes,
    },
    {
      throwOnError: false,
      refetchInterval: 1000 * 30,
    },
  );
  const userQuery = UserQuery.useGetCurrentUserInfoQuery();

  const notificationsOptionsKey = [
    userQuery.data?.options?.isFirmwareNotificationsDisabled,
    userQuery.data?.options?.isQualificationNotificationsDisabled,
    userQuery.data?.options?.isRepairsNotificationsDisabled,
    currentLanguage,
    selectedTypes.join('_'),
  ].join('_');

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    if (!hasInitializedOptionsSync.current) {
      hasInitializedOptionsSync.current = true;
      return;
    }

    // Notification filters changed, rebuild list from the first page.
    // Keep previous items until fresh data arrives to avoid empty modal flicker.
    const shouldInvalidate = offsetRef.current === 0;

    setOffset(0);
    setHasMore(true);

    if (shouldInvalidate) {
      void queryClient.invalidateQueries({
        queryKey: NotificationQuery.getQueryKey({}),
      });
    }
  }, [notificationsOptionsKey]);

  const loadMore = () => {
    if (notificationQuery.isLoading) return;
    setOffset((prev) => prev + PAGE_SIZE);
  };

  const mapNotificationDto = (
    dto: GetNotificationsResponseDto[] | undefined,
  ): Notification[] => {
    if (!dto) return [];

    return dto.map((r) => {
      let params = r.notificationParams;

      const isRepairNotification = r.type === NotificationType.Repairs;

      if (isRepairNotification) {
        const status = getStatus(r.notificationParams?.status);

        const statusName = tDynamic(i18nRepairs, status?.key, {
          defaultValue: r.notificationParams?.status,
        });

        params = {
          ...r.notificationParams,
          status: statusName,
        };
      }

      return {
        id: r.id,
        title: r.title,
        text: r.text,
        notificationParams: params,
        url: r.url,
        type: r.type,
        createdDate: new Date(r.createdDate),
      };
    });
  };

  useEffect(() => {
    if (!notificationQuery.data) return;

    // it's infinite scroll, that's why we don't use select
    const mapped = mapNotificationDto(notificationQuery.data);

    setItems((prev) => (offset === 0 ? mapped : [...prev, ...mapped]));

    setHasMore(mapped.length === PAGE_SIZE);
  }, [notificationQuery.data, offset]);

  const isErrorHappened = notificationQuery.isError;

  const notifications = useMemo(
    () => mapNotificationDto(notificationQuery.data),
    [notificationQuery.data],
  );

  const lastReadTimestamp =
    userQuery.data?.options?.lastReadNotificationDate ?? 0;

  const unreadCount = notifications.filter((notification) => {
    return notification.createdDate.getTime() > lastReadTimestamp;
  }).length;

  const oneMoreNotificationQuery = NotificationQuery.useGetQuery(
    {
      language: currentLanguage,
      limit: 1,
      offset: PAGE_SIZE,
      types: selectedTypes,
    },
    {
      throwOnError: false,
      enabled: unreadCount === PAGE_SIZE,
    },
  );

  const hasUnreadAfterQuery =
    !!oneMoreNotificationQuery.data?.length &&
    new Date(oneMoreNotificationQuery.data[0].createdDate).getTime() >
      lastReadTimestamp;

  const unreadBadgeCount =
    unreadCount === PAGE_SIZE && hasUnreadAfterQuery
      ? PAGE_SIZE + 1
      : unreadCount;

  const handleNotificationClick = (notification: Notification) => {
    if (notification.url) {
      setIsNotificationModalShown(false);
      setSelectedTypes([]);
      void navigate(notification.url, {
        state: refetch,
      });

      if (notification.url === window.location.pathname) {
        const index = notification.url.lastIndexOf('/') + 1;
        const requestNumber = notification.url.slice(index);
        void queryClient.invalidateQueries({
          queryKey:
            RepairsQuery.getDetailsByIncomingNumberQueryKey(requestNumber),
        });
      }
    }
  };

  return (
    <div>
      <IconButton
        className={clsx(
          styles.borderRadius,
          styles.marginRight16,
          isNotificationModalShown && styles.selected,
        )}
        testId={'notification-icon'}
        icon={NotificationIcon}
        badge={unreadBadgeCount}
        badgeMax={PAGE_SIZE}
        badgeTestId="notification-badge"
        isErrorBadge={isErrorHappened}
        iconClassName={styles.icon}
        onClick={() => {
          setIsNotificationModalShown(true);
        }}
      />
      {isNotificationModalShown && (
        <NotificationsModal
          notifications={items}
          loadMore={loadMore}
          hasMore={hasMore}
          onNotificationClick={handleNotificationClick}
          isErrorHappened={isErrorHappened}
          selectedTypes={selectedTypes}
          onSelectedTypesChange={setSelectedTypes}
          onClose={() => {
            setIsNotificationModalShown(false);
            setSelectedTypes([]);
          }}
        />
      )}
    </div>
  );
};
